import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button/Button';
import { AccountChip } from '../account/AccountChip';
import { useCurrentUser } from '../account/useAuth';
import { AppFooter } from '../branding/AppFooter';
import { BrandLockup } from '../branding/branding';
import { PrivacyNote } from '../components/PrivacyNote/PrivacyNote';
import { capture } from '../lib/analytics';
import { ClaimUsernameDialog } from '../profile/ClaimUsernameDialog';
import { fetchMyPublishedApps, type PublishedAppSummary } from '../publish/publishApi';
import { useProjects, useProjectsStoreApi } from '../stores/projectsContext';
import { ClassroomBanner } from './ClassroomBanner';
import { CreateProjectDialog } from './CreateProjectDialog';
import { ProjectCard } from './ProjectCard';
import { UndoToast } from './UndoToast';
import './Home.css';

function classifyReferrer(ref: string): 'direct' | 'remix' | 'share' | 'search' | 'other' {
  if (!ref) return 'direct';
  try {
    const url = new URL(ref);
    if (url.hostname === window.location.hostname) {
      if (url.pathname.startsWith('/gallery')) return 'remix';
      if (url.pathname.startsWith('/p/')) return 'share';
      return 'other';
    }
    if (/google|bing|duckduckgo|yahoo|brave/i.test(url.hostname)) return 'search';
    return 'other';
  } catch {
    return 'other';
  }
}

export function Home(): JSX.Element {
  const projects = useProjects((s) => s.projects);
  const loading = useProjects((s) => s.loading);
  const error = useProjects((s) => s.error);
  const pendingDelete = useProjects((s) => s.pendingDelete);
  const store = useProjectsStoreApi();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  // Only the project-scoped undo toast belongs on Home; class / samples
  // toasts live next to the user's view of those things.
  const projectPending = pendingDelete?.kind === 'project' ? pendingDelete : null;

  const [creating, setCreating] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Username claim dialog: opens when AccountChip nudges the kid via
  // `#claim-username` in the URL hash, OR (auto-prompt) the first time
  // a signed-in user without a username lands here. Closing dismisses
  // the prompt for this tab; the dialog re-opens when they revisit
  // explicitly through the chip.
  const location = useLocation();
  const [claimOpen, setClaimOpen] = useState(false);
  const [autoPromptShown, setAutoPromptShown] = useState(false);
  useEffect(() => {
    if (location.hash === '#claim-username') {
      setClaimOpen(true);
    }
  }, [location.hash]);
  useEffect(() => {
    if (!autoPromptShown && user && !user.username && !user.is_child) {
      setClaimOpen(true);
      setAutoPromptShown(true);
    }
  }, [autoPromptShown, user]);

  useEffect(() => {
    void store.getState().load();
  }, [store]);

  // `studio_visited` — head of the activation funnel. Fired once per
  // mount; React Router treats /studio as a single mount across navs
  // back to it via the SPA shell, but a hard reload counts as a new
  // session, which is what we want.
  useEffect(() => {
    capture('studio_visited', { referrer_kind: classifyReferrer(document.referrer) });
  }, []);

  // Map of projectId → published-app summary. Drives the
  // "Published v3" chip on ProjectCard so the kid sees status
  // without clicking in. Refetched whenever the user changes (so a
  // fresh sign-in brings in their other-device publishes).
  const [publishedByProject, setPublishedByProject] = useState<Record<string, PublishedAppSummary>>(
    {},
  );
  useEffect(() => {
    if (!user) {
      setPublishedByProject({});
      return;
    }
    let cancelled = false;
    void fetchMyPublishedApps()
      .then((apps) => {
        if (cancelled || !apps) return;
        const map: Record<string, PublishedAppSummary> = {};
        for (const a of apps) map[a.projectId] = a;
        setPublishedByProject(map);
      })
      .catch(() => {
        // Non-fatal — chip just doesn't render. Kid's projects still load.
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleCreate = async (input: {
    name: string;
    kind: 'image' | 'audio' | 'pose';
  }): Promise<void> => {
    const project = await store.getState().create(input);
    setCreating(false);
    navigate(`/studio/p/${project.id}`);
  };

  const handleOpenFile = async (file: File): Promise<void> => {
    setImportError(null);
    try {
      const project = await store.getState().importProjectFile(file);
      navigate(`/studio/p/${project.id}`);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Could not open that file.');
    }
  };

  return (
    <div className="bl-page">
      <div className="bl-home">
        <header className="bl-home__header">
          <BrandLockup subBrand="Studio" />
          <div className="bl-home__header-right">
            <PrivacyNote state="on-device" />
            <AccountChip />
            <Link
              to="/gallery"
              className="bl-home__about"
              data-testid="home-gallery-link"
              title="See projects other kids have shared"
            >
              Gallery
            </Link>
            {user && (
              <button
                type="button"
                className="bl-home__about"
                onClick={() => navigate('/studio/me/published')}
                title="See everything you've published to BytaApps"
              >
                Published
              </button>
            )}
            <button type="button" className="bl-home__about" onClick={() => navigate('/')}>
              About
            </button>
          </div>
        </header>

        {user?.classroom_id ? <ClassroomBanner classroomId={user.classroom_id} /> : null}

        <section className="bl-home__hero">
          <h1 className="bl-home__title">Your projects</h1>
          <div className="bl-home__hero-actions">
            {user?.classroom_id ? (
              <Link to="/me/feedback" className="bl-home__open" data-testid="feedback-link">
                Feedback
              </Link>
            ) : null}
            {user ? (
              <Link to="/portfolio" className="bl-home__open" data-testid="portfolio-link">
                ★ Portfolio
              </Link>
            ) : null}
            <button
              type="button"
              className="bl-home__open"
              onClick={() => fileInputRef.current?.click()}
            >
              Open project file
            </button>
            <Button onClick={() => setCreating(true)}>+ New project</Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".bryta,application/zip"
            aria-label="Open a BytaApps project file"
            className="bl-home__file-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleOpenFile(file);
              e.target.value = '';
            }}
          />
        </section>

        {error && (
          <p role="alert" className="bl-home__error">
            {error}
          </p>
        )}
        {importError && (
          <p role="alert" className="bl-home__error">
            {importError}
          </p>
        )}

        {loading && projects.length === 0 ? (
          <p className="bl-home__loading" aria-live="polite">
            Looking for your projects…
          </p>
        ) : projects.length === 0 ? (
          <EmptyState onCreate={() => setCreating(true)} onTour={() => navigate('/onboarding')} />
        ) : (
          <ul className="bl-home__list" aria-label="Saved projects">
            {projects.map((project) => {
              const pub = publishedByProject[project.id];
              return (
                <li key={project.id}>
                  <ProjectCard
                    project={project}
                    onOpen={(id) => navigate(`/studio/p/${id}`)}
                    onDelete={(id) => store.getState().scheduleProjectDelete(id)}
                    {...(pub ? { publishedVersion: pub.version, publishedUrl: pub.url } : {})}
                  />
                </li>
              );
            })}
          </ul>
        )}

        <CreateProjectDialog
          isOpen={creating}
          onClose={() => setCreating(false)}
          onCreate={handleCreate}
        />

        {projectPending && (
          <UndoToast
            message={`Deleted "${projectPending.project.name}"`}
            expiresAt={projectPending.expiresAt}
            onUndo={() => store.getState().undoDelete()}
          />
        )}
        <ClaimUsernameDialog
          isOpen={claimOpen}
          initialValue={user?.email?.split('@')[0]?.toLowerCase()}
          onClose={() => setClaimOpen(false)}
        />
      </div>
      <AppFooter />
    </div>
  );
}

function EmptyState({
  onCreate,
  onTour,
}: {
  onCreate: () => void;
  onTour: () => void;
}): JSX.Element {
  return (
    <div className="bl-home__empty">
      <h2>Nothing here yet</h2>
      <p>
        Make your first model. Show your computer something with the camera or mic and it learns
        what you showed it.
      </p>
      <div className="bl-home__empty-actions">
        <Button onClick={onTour}>Take the 15-min tour</Button>
        <button type="button" className="bl-home__empty-skip" onClick={onCreate}>
          Or start from scratch
        </button>
      </div>
    </div>
  );
}
